import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@bliks-io/ui';
import type { EntitySuggestionKind } from '@bliks-io/contracts';

interface MergeConfirmDialogProps {
  open: boolean;
  kind: EntitySuggestionKind;
  /** Display name of the target existing entity to merge into. */
  targetName: string;
  /** Display name of the proposed (new) entity being merged away. */
  suggestionName: string;
  isMerging: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation step shown after the user picks an existing entity to merge
 * into. Merging resolves the suggestion permanently into the chosen entity
 * and updates the source recording, so a confirm step prevents accidental
 * merges from a stray click on the duplicate-candidates list or selector.
 */
export function MergeConfirmDialog({
  open,
  kind,
  targetName,
  suggestionName,
  isMerging,
  onCancel,
  onConfirm
}: MergeConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={t(`entitySuggestion.review.confirmMerge.title.${kind}`)}
      description={t(`entitySuggestion.review.confirmMerge.description.${kind}`, {
        suggestionName,
        targetName
      })}
      confirmLabel={t('entitySuggestion.review.confirmMerge.confirm')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isPending={isMerging}
    />
  );
}
